import React, { FC, useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: ReactNode;
  content: string;
  enabled?: boolean;
}

const Tooltip: FC<TooltipProps> = ({ children, content, enabled = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      
      // Safety check: if the rect is all zeros (common with display: contents or hidden elements)
      if (rect.width === 0 && rect.height === 0) return;

      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    }
  };

  useEffect(() => {
    if (isVisible) {
      updateCoords();
      window.addEventListener('scroll', updateCoords, true);
      window.addEventListener('resize', updateCoords);
      
      // Periodic check for dynamic height changes/animations
      const interval = setInterval(updateCoords, 50);
      
      return () => {
        window.removeEventListener('scroll', updateCoords, true);
        window.removeEventListener('resize', updateCoords);
        clearInterval(interval);
      };
    }
    return undefined;
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (enabled) {
      updateCoords();
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className='w-full h-fit flex flex-col'
      >
        {children}
      </div>
      {isVisible && enabled && coords.top !== 0 && createPortal(
        <div
          className='fixed z-[9999] pointer-events-none animate-in fade-in zoom-in-95 duration-200'
          style={{
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {/* Arrow */}
          <div className='absolute left-[-6px] top-1/2 -translate-y-1/2 border-[6px] border-transparent border-right-slate-800 dark:border-right-slate-700' />
          
          {/* Content */}
          <div className='bg-slate-800 dark:bg-slate-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap'>
            {content}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
